/**
 * Popup wording for PLANNED works. A planned job is not a fault, so it should not read like
 * one: no "Duration: 5d 1h ago", no "No restoration estimate published yet", no worsening/
 * improving trend on a customer count. Instead it leads with when the works start (or started)
 * and when the operator expects them to finish, and says plainly that these are planned works
 * with the operator's own times. Pure (no DOM), unit tested in test/web/plannedPopup.test.ts.
 */

import {
  ICON_CLOCK, ICON_PEOPLE_FILL, TIME_OPTS, filledIcon, formatDateTime, formatDuration, metaRow, outlineIcon,
} from './popupFormat.ts';

export interface PlannedPopupIncident {
  readonly cause: string;
  readonly status: string;
  readonly scheduledFor: string | null;
  readonly startedAt: string | null;
  readonly restoredAt: string | null;
  readonly customersAffected: number | null;
  readonly estimatedRestoration: {
    readonly claim:
      | { readonly kind: 'none' | 'to-be-confirmed' }
      | { readonly kind: 'point'; readonly at: string }
      | { readonly kind: 'window'; readonly from: string; readonly to: string };
    readonly displayText: string | null;
  };
}

/** Planned works that have not finished (in progress, or scheduled for later). */
export function isOpenPlanned(incident: Pick<PlannedPopupIncident, 'cause' | 'status'>): boolean {
  return incident.cause === 'planned' && incident.status !== 'restored';
}

/** Escapes operator-supplied text, same discipline as main.ts's escapeHtml. */
function escape(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

/** When the works start (future) or started (past); scheduledFor wins over startedAt. */
function startRow(incident: PlannedPopupIncident, now: number): string {
  const icon = outlineIcon(ICON_CLOCK);
  const at = incident.scheduledFor ?? incident.startedAt;
  if (at === null) return metaRow('Works start', 'Not published', true, icon);
  const startsLater = new Date(at).getTime() > now;
  const label = startsLater ? 'Starts' : 'Started';
  const value = startsLater
    ? `${formatDateTime(at)} &middot; in ${formatDuration((new Date(at).getTime() - now) / 60_000)}`
    : formatDateTime(at);
  return metaRow(label, value, false, icon);
}

function finishRow(incident: PlannedPopupIncident): string {
  const icon = outlineIcon(ICON_CLOCK);
  const { claim, displayText } = incident.estimatedRestoration;
  if (displayText) return metaRow('Expected finish', escape(displayText), false, icon);
  if (claim.kind === 'point') return metaRow('Expected finish', formatDateTime(claim.at), false, icon);
  if (claim.kind === 'window') {
    const from = new Date(claim.from).toLocaleTimeString('en-GB', TIME_OPTS);
    const to = new Date(claim.to).toLocaleTimeString('en-GB', TIME_OPTS);
    return metaRow('Expected finish', `between ${from} and ${to}`, false, icon);
  }
  return metaRow('Expected finish', 'Not published', true, icon);
}

function customersRow(incident: PlannedPopupIncident): string {
  const icon = filledIcon(ICON_PEOPLE_FILL, '#0B1F3B');
  // A plain count: "worsening/improving" describes a fault developing, not a job going to plan.
  if (incident.customersAffected === null) return metaRow('Customers affected', 'Not published', true, icon);
  return metaRow('Customers affected', incident.customersAffected.toLocaleString('en-GB'), false, icon);
}

/** The meta block for an open planned job. */
export function plannedMetaRows(incident: PlannedPopupIncident, now: number): string {
  return `${startRow(incident, now)}${finishRow(incident)}${customersRow(incident)}`;
}

/** The line under the meta block. */
export const PLANNED_FOOTER = 'Planned works by the operator, not a fault. Times are the operator\'s own.';

/** For planned works that have finished. */
export function plannedCompletedText(incident: Pick<PlannedPopupIncident, 'restoredAt'>): string {
  return incident.restoredAt === null
    ? 'Planned works completed.'
    : `Planned works completed at ${formatDateTime(incident.restoredAt)}`;
}
